import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { endpoints } from '../api/endpoints';
import type { Scenario, ScenarioContent, ScenarioStatus } from '../api/types';
import { ErrorBox, formatDate, Loading, Notice, useAsync } from '../components/common';
import { GraphEditor } from '../components/scenario/GraphEditor';
import { emptyContent, normalizeContent, validateGraph } from '../components/scenario/graph';

export function ScenarioEditorPage() {
  const { id } = useParams();
  const isNew = !id || id === 'new';
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const loaded = useAsync<Scenario | null>(() => (isNew ? Promise.resolve(null) : endpoints.scenarios.get(id as string)), [id]);
  const versions = useAsync(() => (isNew ? Promise.resolve([]) : endpoints.scenarios.versions(id as string)), [id]);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [tags, setTags] = useState('');
  const [status, setStatus] = useState<ScenarioStatus>('draft');
  const [duration, setDuration] = useState<number | ''>('');
  const [content, setContent] = useState<ScenarioContent>(emptyContent());
  const [jsonMode, setJsonMode] = useState(false);
  const [jsonText, setJsonText] = useState('');
  const [bump, setBump] = useState<'major' | 'minor' | 'patch'>('minor');
  const [error, setError] = useState<unknown>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    const s = loaded.data;
    if (!s) return;
    setTitle(s.title);
    setDescription(s.description ?? '');
    setTags((s.tags ?? []).join(', '));
    setStatus(s.status);
    setDuration(s.estimated_duration_sec ?? '');
    setContent(normalizeContent(s.content));
  }, [loaded.data]);

  const issues = validateGraph(content);
  const problems = issues.map((i) => t(`graphEditor.issues.${i.code}`, { node: i.node ?? '' }));
  const meta = () => ({
    title,
    description: description || null,
    tags: tags.split(',').map((x) => x.trim()).filter(Boolean),
    status,
    estimated_duration_sec: duration === '' ? null : Number(duration),
  });

  const run = async (fn: () => Promise<void>, message?: string) => {
    setError(null);
    setNotice(null);
    try {
      await fn();
      if (message) setNotice(message);
    } catch (err) {
      setError(err);
    }
  };

  const save = () =>
    run(async () => {
      if (isNew) {
        const created = await endpoints.scenarios.create({ ...meta(), content });
        navigate(`/scenarios/${created.id}`, { replace: true });
        return;
      }
      await endpoints.scenarios.update(id as string, meta());
      await loaded.reload();
    }, t('common.saved'));

  const saveVersion = () =>
    run(async () => {
      await endpoints.scenarios.createVersion(id as string, { content, bump });
      await Promise.all([loaded.reload(), versions.reload()]);
    }, t('common.saved'));

  if (loaded.loading) return <Loading />;

  return (
    <div>
      <h1>{t('scenarios.edit')}</h1>
      <ErrorBox error={loaded.error || error} />
      {notice && <Notice>{notice}</Notice>}
      <div className="card form grid-2">
        <label>
          {t('scenarios.titleField')}
          <input required value={title} onChange={(e) => setTitle(e.target.value)} />
        </label>
        <label>
          {t('scenarios.statusField')}
          <select value={status} onChange={(e) => setStatus(e.target.value as ScenarioStatus)}>
            {(['draft', 'published', 'archived'] as const).map((s) => (
              <option key={s} value={s}>
                {t(`scenarios.status.${s}`)}
              </option>
            ))}
          </select>
        </label>
        <label className="span-2">
          {t('scenarios.description')}
          <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={2} />
        </label>
        <label>
          {t('scenarios.tags')}
          <input value={tags} onChange={(e) => setTags(e.target.value)} />
        </label>
        <label>
          {t('scenarios.estimatedDuration')}
          <input type="number" min={0} value={duration} onChange={(e) => setDuration(e.target.value === '' ? '' : Number(e.target.value))} />
        </label>
        <div className="actions span-2">
          <button type="button" disabled={!title || problems.length > 0} onClick={() => void save()}>
            {t('common.save')}
          </button>
          {loaded.data && (
            <span className="muted">
              {t('scenarios.version')}: {loaded.data.semantic_version ?? loaded.data.version}
            </span>
          )}
        </div>
      </div>

      <section className="card">
        <div className="page-header">
          <h2>{t('scenarios.graph')}</h2>
          <label>
            <input
              type="checkbox"
              checked={jsonMode}
              onChange={(e) => {
                setJsonMode(e.target.checked);
                setJsonText(JSON.stringify(content, null, 2));
              }}
            />
            {t('scenarios.jsonMode')}
          </label>
        </div>
        <p role="status" className="muted">
          {problems.length ? t('graphEditor.problemCount', { n: problems.length }) : t('graphEditor.valid')}
        </p>
        {problems.length > 0 && (
          <ul aria-label={t('graphEditor.problems')} className="alert alert-error problems">
            {problems.map((p, i) => (
              <li key={i}>{p}</li>
            ))}
          </ul>
        )}
        {jsonMode ? (
          <div className="form">
            <textarea aria-label={t('scenarios.jsonMode')} rows={16} value={jsonText} onChange={(e) => setJsonText(e.target.value)} />
            <button
              type="button"
              onClick={() => {
                try {
                  setContent(normalizeContent(JSON.parse(jsonText)));
                  setError(null);
                } catch {
                  setError(new Error(t('scenarios.invalidJson')));
                }
              }}
            >
              {t('common.save')}
            </button>
          </div>
        ) : (
          <GraphEditor content={content} onChange={setContent} issues={issues} />
        )}
        {!isNew && (
          <div className="actions">
            <label>
              {t('scenarios.bump')}
              <select value={bump} onChange={(e) => setBump(e.target.value as typeof bump)}>
                <option value="major">{t('scenarios.bumpMajor')}</option>
                <option value="minor">{t('scenarios.bumpMinor')}</option>
                <option value="patch">{t('scenarios.bumpPatch')}</option>
              </select>
            </label>
            <button type="button" disabled={problems.length > 0} onClick={() => void saveVersion()}>
              {t('scenarios.saveVersion')}
            </button>
            <button
              type="button"
              className="secondary"
              onClick={() =>
                void run(async () => {
                  await endpoints.templates.create({ name: title, description, content });
                }, t('common.saved'))
              }
            >
              {t('scenarios.saveAsTemplate')}
            </button>
          </div>
        )}
      </section>

      {!isNew && (
        <section className="card">
          <h2>{t('scenarios.versions')}</h2>
          <ErrorBox error={versions.error} onRetry={versions.reload} />
          <table>
            <thead>
              <tr>
                <th scope="col">{t('scenarios.version')}</th>
                <th scope="col">{t('sessions.started')}</th>
                <th scope="col">{t('admin.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {(versions.data ?? []).map((v) => (
                <tr key={v.version}>
                  <td>{v.semantic_version ?? v.version}</td>
                  <td>{formatDate(v.changed_at, i18n.language)}</td>
                  <td>
                    <button
                      type="button"
                      className="secondary"
                      onClick={() =>
                        void run(async () => {
                          await endpoints.scenarios.rollback(id as string, v.version);
                          await Promise.all([loaded.reload(), versions.reload()]);
                        }, t('common.saved'))
                      }
                    >
                      {t('scenarios.rollback')}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}
    </div>
  );
}
